<script setup>
import { computed, onMounted, onUnmounted, reactive, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import QRCode from 'qrcode';
import EmptyState from '../../../../components/widgets/EmptyState.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DuplicateInboxBanner from './channels/instagram/DuplicateInboxBanner.vue';
import EmailInboxFinish from './channels/emailChannels/EmailInboxFinish.vue';
import { useInbox } from 'dashboard/composables/useInbox';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { useDinastiaQr } from 'dashboard/composables/useDinastiaQr';

const { t } = useI18n();
const route = useRoute();
const store = useStore();

const qrCodes = reactive({
  whatsapp: '',
  messenger: '',
  telegram: '',
});

// Dinastia WhatsApp state — shared composable
const {
  qrImage: dinastiaQr,
  phoneNumber: dinastiaPhone,
  isActionLoading: dinastiaActionLoading,
  showLoading: dinastiaShowLoading,
  showQr: dinastiaShowQr,
  showConnected: dinastiaShowConnected,
  showDisconnected: dinastiaShowDisconnected,
  startPolling: startDinastiaPoll,
  connect: handleDinastiaConnect,
  cleanup: cleanupDinastia,
} = useDinastiaQr();

const currentInbox = computed(() =>
  store.getters['inboxes/getInbox'](route.params.inbox_id)
);

const {
  isAWhatsAppCloudChannel,
  isATwilioChannel,
  isASmsInbox,
  isALineChannel,
  isAnEmailChannel,
  isAWhatsAppChannel,
  isAFacebookInbox,
  isATelegramChannel,
  isATwilioWhatsAppChannel,
  isADinastiaWhatsappChannel,
} = useInbox(route.params.inbox_id);

const hasDuplicateInstagramInbox = computed(() => {
  const instagramId = currentInbox.value.instagram_id;
  const facebookInbox =
    store.getters['inboxes/getFacebookInboxByInstagramId'](instagramId);

  return (
    currentInbox.value.channel_type === INBOX_TYPES.INSTAGRAM && facebookInbox
  );
});

const shouldShowWhatsAppWebhookDetails = computed(() => {
  return (
    isAWhatsAppCloudChannel.value &&
    currentInbox.value.provider_config?.source !== 'embedded_signup'
  );
});

const isWhatsAppEmbeddedSignup = computed(() => {
  return (
    isAWhatsAppCloudChannel.value &&
    currentInbox.value.provider_config?.source === 'embedded_signup'
  );
});

// Dinastia computed states are provided by useDinastiaQr composable

const message = computed(() => {
  if (isADinastiaWhatsappChannel.value) {
    return t('INBOX_MGMT.FINISH.DINASTIA_SCAN_QR');
  }

  if (isATwilioChannel.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.TWILIO.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (isASmsInbox.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.SMS.BANDWIDTH.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (isALineChannel.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (isAWhatsAppCloudChannel.value && shouldShowWhatsAppWebhookDetails.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.WHATSAPP.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (currentInbox.value.web_widget_script) {
    return t('INBOX_MGMT.FINISH.WEBSITE_SUCCESS');
  }

  if (isWhatsAppEmbeddedSignup.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.FINISH.WHATSAPP_QR_INSTRUCTION'
    )}`;
  }

  return t('INBOX_MGMT.FINISH.MESSAGE');
});

async function generateQRCode(platform, identifier) {
  if (!identifier || !identifier.trim()) {
    return;
  }

  try {
    const platformUrls = {
      whatsapp: id => `https://wa.me/${id}`,
      messenger: id => `https://m.me/${id}`,
      telegram: id => `https://t.me/${id}`,
    };

    const url = platformUrls[platform](identifier);
    const qrDataUrl = await QRCode.toDataURL(url);
    qrCodes[platform] = qrDataUrl;
  } catch {
    qrCodes[platform] = '';
  }
}

async function generateQRCodes() {
  if (!currentInbox.value) return;

  // Skip generic WhatsApp QR for Dinastia channels
  if (
    currentInbox.value.phone_number &&
    isAWhatsAppChannel.value &&
    !isADinastiaWhatsappChannel.value
  ) {
    const phoneNumber = currentInbox.value.phone_number.replace(
      'whatsapp:',
      ''
    );
    await generateQRCode('whatsapp', phoneNumber);
  }

  if (currentInbox.value.page_id && isAFacebookInbox.value) {
    await generateQRCode('messenger', currentInbox.value.page_id);
  }

  if (isATelegramChannel.value && currentInbox.value.bot_name) {
    await generateQRCode('telegram', currentInbox.value.bot_name);
  }
}

// Dinastia WhatsApp session management is handled by useDinastiaQr composable

watch(
  isADinastiaWhatsappChannel,
  val => {
    if (val) {
      startDinastiaPoll(route.params.inbox_id);
    }
  },
  { immediate: true }
);

watch(
  currentInbox,
  newInbox => {
    if (newInbox) {
      generateQRCodes();
    }
  },
  { immediate: true }
);

onMounted(() => {
  generateQRCodes();
});

onUnmounted(() => {
  cleanupDinastia();
});
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <!-- Dinastia WhatsApp: Custom QR Code Experience -->
    <div
      v-if="isADinastiaWhatsappChannel"
      class="flex flex-col items-center justify-center min-h-[60vh] gap-8 py-8"
    >
      <!-- Header -->
      <div class="flex flex-col items-center gap-3 text-center">
        <div
          class="flex items-center justify-center size-14 rounded-2xl bg-n-teal-3"
        >
          <span class="i-ri-whatsapp-fill text-3xl text-n-teal-11" />
        </div>
        <h2 class="text-xl font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.FINISH.DINASTIA_TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-10 max-w-md mb-0">
          {{ $t('INBOX_MGMT.FINISH.DINASTIA_SUBTITLE') }}
        </p>
      </div>

      <!-- QR Card -->
      <div
        class="rounded-2xl border border-n-weak bg-n-solid-2 shadow-lg w-full max-w-sm"
      >
        <!-- Loading State -->
        <div
          v-if="dinastiaShowLoading"
          class="flex flex-col items-center gap-4 p-12"
        >
          <div class="relative">
            <span
              class="i-lucide-loader-2 animate-spin text-4xl text-n-brand"
            />
          </div>
          <p class="text-sm text-n-slate-10 mb-0">
            {{ $t('INBOX_MGMT.FINISH.DINASTIA_WAITING') }}
          </p>
        </div>

        <!-- QR Code -->
        <div
          v-else-if="dinastiaShowQr"
          class="flex flex-col items-center gap-4 p-6"
        >
          <div class="rounded-xl bg-white p-3">
            <img :src="dinastiaQr" alt="WhatsApp QR Code" class="size-64" />
          </div>
          <div class="flex items-center gap-1.5 text-xs text-n-slate-8">
            <span class="i-lucide-refresh-cw text-xs" />
            <span>{{ $t('INBOX_MGMT.FINISH.DINASTIA_QR_REFRESHES') }}</span>
          </div>
          <button
            type="button"
            class="text-xs text-n-brand hover:text-n-brand/80 transition-colors"
            @click="handleDinastiaConnect($route.params.inbox_id)"
          >
            {{ $t('INBOX_MGMT.FINISH.DINASTIA_REGENERATE_QR') }}
          </button>
        </div>

        <!-- Connected -->
        <div
          v-else-if="dinastiaShowConnected"
          class="flex flex-col items-center gap-4 p-10"
        >
          <div
            class="flex items-center justify-center size-16 rounded-full bg-n-teal-3"
          >
            <span class="i-lucide-check text-3xl text-n-teal-11" />
          </div>
          <div class="text-center">
            <p class="text-base font-semibold text-n-teal-11 mb-1">
              {{ $t('INBOX_MGMT.FINISH.DINASTIA_CONNECTED') }}
            </p>
            <p v-if="dinastiaPhone" class="text-sm text-n-slate-10 mb-0">
              {{ dinastiaPhone }}
            </p>
          </div>
        </div>

        <!-- Disconnected / Error -->
        <div
          v-else-if="dinastiaShowDisconnected"
          class="flex flex-col items-center gap-4 p-10"
        >
          <div
            class="flex items-center justify-center size-14 rounded-full bg-n-ruby-3"
          >
            <span class="i-lucide-wifi-off text-2xl text-n-ruby-11" />
          </div>
          <p class="text-sm text-n-slate-10 mb-0">
            {{ $t('INBOX_MGMT.FINISH.DINASTIA_CONNECTION_LOST') }}
          </p>
          <NextButton
            solid
            teal
            :label="$t('INBOX_MGMT.FINISH.DINASTIA_RETRY')"
            :is-loading="dinastiaActionLoading"
            icon="i-lucide-refresh-cw"
            @click="handleDinastiaConnect($route.params.inbox_id)"
          />
        </div>
      </div>

      <!-- Instructions -->
      <div
        v-if="!dinastiaShowConnected"
        class="flex flex-col gap-3 max-w-sm w-full"
      >
        <div class="flex items-start gap-3">
          <span
            class="flex items-center justify-center size-6 rounded-full bg-n-brand/10 text-xs font-bold text-n-brand shrink-0"
          >
            {{ 1 }}
          </span>
          <p class="text-sm text-n-slate-11 mb-0 pt-0.5">
            {{ $t('INBOX_MGMT.FINISH.DINASTIA_STEP_1') }}
          </p>
        </div>
        <div class="flex items-start gap-3">
          <span
            class="flex items-center justify-center size-6 rounded-full bg-n-brand/10 text-xs font-bold text-n-brand shrink-0"
          >
            {{ 2 }}
          </span>
          <p class="text-sm text-n-slate-11 mb-0 pt-0.5">
            {{ $t('INBOX_MGMT.FINISH.DINASTIA_STEP_2') }}
          </p>
        </div>
        <div class="flex items-start gap-3">
          <span
            class="flex items-center justify-center size-6 rounded-full bg-n-brand/10 text-xs font-bold text-n-brand shrink-0"
          >
            {{ 3 }}
          </span>
          <p class="text-sm text-n-slate-11 mb-0 pt-0.5">
            {{ $t('INBOX_MGMT.FINISH.DINASTIA_STEP_3') }}
          </p>
        </div>
      </div>

      <!-- Navigation Buttons -->
      <div class="flex gap-3">
        <router-link
          :to="{
            name: 'settings_inboxes_add_agents',
            params: { page: 'new', inbox_id: $route.params.inbox_id },
          }"
        >
          <NextButton
            outline
            slate
            :label="$t('INBOX_MGMT.ADD.AGENTS.TITLE')"
            icon="i-lucide-users"
          />
        </router-link>
        <router-link
          :to="{
            name: 'settings_inbox_show',
            params: { inboxId: $route.params.inbox_id },
          }"
        >
          <NextButton
            outline
            slate
            :label="$t('INBOX_MGMT.FINISH.MORE_SETTINGS')"
          />
        </router-link>
        <router-link
          :to="{
            name: 'inbox_dashboard',
            params: { inboxId: $route.params.inbox_id },
          }"
        >
          <NextButton solid teal :label="$t('INBOX_MGMT.FINISH.BUTTON_TEXT')" />
        </router-link>
      </div>
    </div>

    <!-- All Other Channels: Original EmptyState -->
    <template v-else>
      <DuplicateInboxBanner
        v-if="hasDuplicateInstagramInbox"
        :content="$t('INBOX_MGMT.ADD.INSTAGRAM.NEW_INBOX_SUGGESTION')"
      />
      <EmptyState
        :title="$t('INBOX_MGMT.FINISH.TITLE')"
        :message="isAnEmailChannel && !currentInbox.provider ? '' : message"
        :button-text="$t('INBOX_MGMT.FINISH.BUTTON_TEXT')"
      >
        <div class="w-full text-center">
          <div class="my-4 mx-auto max-w-[70%]">
            <woot-code
              v-if="currentInbox.web_widget_script"
              :script="currentInbox.web_widget_script"
            />
          </div>
          <div class="w-[50%] max-w-[50%] ml-[25%]">
            <woot-code
              v-if="isATwilioWhatsAppChannel"
              lang="html"
              :script="currentInbox.callback_webhook_url"
            />
          </div>
          <div
            v-if="shouldShowWhatsAppWebhookDetails"
            class="w-[50%] max-w-[50%] ml-[25%]"
          >
            <p class="mt-8 font-medium text-n-slate-11">
              {{ $t('INBOX_MGMT.ADD.WHATSAPP.API_CALLBACK.WEBHOOK_URL') }}
            </p>
            <woot-code
              lang="html"
              :script="currentInbox.callback_webhook_url"
            />
            <p class="mt-8 font-medium text-n-slate-11">
              {{
                $t(
                  'INBOX_MGMT.ADD.WHATSAPP.API_CALLBACK.WEBHOOK_VERIFICATION_TOKEN'
                )
              }}
            </p>
            <woot-code
              lang="html"
              :script="currentInbox.provider_config.webhook_verify_token"
            />
          </div>
          <div class="w-[50%] max-w-[50%] ml-[25%]">
            <woot-code
              v-if="isALineChannel"
              lang="html"
              :script="currentInbox.callback_webhook_url"
            />
          </div>
          <div class="w-[50%] max-w-[50%] ml-[25%]">
            <woot-code
              v-if="isASmsInbox"
              lang="html"
              :script="currentInbox.callback_webhook_url"
            />
          </div>
          <EmailInboxFinish
            v-if="isAnEmailChannel && !currentInbox.provider"
            :inbox="currentInbox"
            :inbox-id="$route.params.inbox_id"
          />

          <div
            v-if="isAWhatsAppChannel && qrCodes.whatsapp"
            class="flex flex-col gap-3 items-center mt-8"
          >
            <p class="mt-2 text-sm text-n-slate-9">
              {{ $t('INBOX_MGMT.FINISH.WHATSAPP_QR_INSTRUCTION') }}
            </p>
            <div class="rounded-lg shadow outline-1 outline-n-strong outline">
              <img
                :src="qrCodes.whatsapp"
                alt="WhatsApp QR Code"
                class="rounded-lg size-48 dark:invert"
              />
            </div>
          </div>
          <div
            v-if="isAFacebookInbox && qrCodes.messenger"
            class="flex flex-col gap-3 items-center mt-8"
          >
            <p class="mt-2 text-sm text-n-slate-9">
              {{ $t('INBOX_MGMT.FINISH.MESSENGER_QR_INSTRUCTION') }}
            </p>
            <div class="rounded-lg shadow outline-1 outline-n-strong outline">
              <img
                :src="qrCodes.messenger"
                alt="Messenger QR Code"
                class="rounded-lg size-48 dark:invert"
              />
            </div>
          </div>
          <div
            v-if="isATelegramChannel && qrCodes.telegram"
            class="flex flex-col gap-4 items-center mt-8"
          >
            <p class="mt-2 text-sm text-n-slate-9">
              {{ $t('INBOX_MGMT.FINISH.TELEGRAM_QR_INSTRUCTION') }}
            </p>

            <div class="rounded-lg shadow outline-1 outline-n-strong outline">
              <img
                :src="qrCodes.telegram"
                alt="Telegram QR Code"
                class="rounded-lg size-48 dark:invert"
              />
            </div>
          </div>
          <div class="flex gap-2 justify-center mt-4">
            <router-link
              :to="{
                name: 'settings_inbox_show',
                params: { inboxId: $route.params.inbox_id },
              }"
            >
              <NextButton
                outline
                slate
                :label="$t('INBOX_MGMT.FINISH.MORE_SETTINGS')"
              />
            </router-link>
            <router-link
              :to="{
                name: 'inbox_dashboard',
                params: { inboxId: $route.params.inbox_id },
              }"
            >
              <NextButton
                solid
                teal
                :label="$t('INBOX_MGMT.FINISH.BUTTON_TEXT')"
              />
            </router-link>
          </div>
        </div>
      </EmptyState>
    </template>
  </div>
</template>
